import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from "@angular/common/http";
import { ChangeDetectorRef, Component, inject, Inject, OnInit, ViewEncapsulation } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { FieldType, FormlyFieldConfig } from "@ngx-formly/core";
import { FileSystemFileEntry, NgxFileDropEntry } from "ngx-file-drop";
import { Observable, throwError as _observableThrow, of as _observableOf, Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { mergeMap as _observableMergeMap } from "rxjs/operators";
import { get, keys } from "lodash";
import { NotifyService } from "@ui/notify";
import { LEGACY_API_BASE_URL } from "@api";

@Component({
    selector: "formly-field-file-uploader",
    templateUrl: "formly-file-uploader.component.html",
    styleUrls: ["./formly-file-uploader.component.scss"],
})
export class FormlyFieldFileUploaderV2Component extends FieldType implements OnInit {
    private _cdr = inject(ChangeDetectorRef);

    loading$: Subject<boolean> = new Subject();

    _unsubscribeAll = new Subject();

    _datasource: any[] = [];

    fileTypes: string[];

    filesWithTypeError: any[] = [];

    maxNumberOfFiles: number;

    required: boolean;

    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(
        private _http: HttpClient,
        private _notifyService: NotifyService,
        private _translocoService: TranslocoService,
        @Inject(LEGACY_API_BASE_URL) private _url?: string
    ) {
        super();
    }

    ngOnInit(): void {
        this.loading$.pipe(takeUntil(this._unsubscribeAll)).subscribe((loading) => {
            if (loading) {
                this.field.formControl.setErrors({
                    loading: true,
                });
            } else {
                this.field.formControl.setErrors(null);
                this.field.formControl.updateValueAndValidity();
            }
        });

        if (!this.formControl.value || (Array.isArray(this.formControl.value) && this.formControl.value.length === 0)) {
            this.formControl.setValue({});
        } else {
            let parsedValue =
                typeof this.formControl.value === "string"
                    ? JSON.parse(this.formControl.value)
                    : this.formControl.value;
            this.formControl.setValue(parsedValue);
        }

        this.datasource = this.formControl.value;
        this.fileTypes = this.field.templateOptions.fileTypes;
        this.maxNumberOfFiles = this.field.templateOptions.maxNumberOfFiles;
        this.required = this.field.templateOptions.required;
        this.formControl.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            this.datasource = value;
        });
    }

    set datasource(value: any) {
        let files = keys(value);
        let items = [];
        for (const fileName of files) {
            items.push({
                [`${fileName}`]: get(value, fileName),
            });
        }
        this._datasource = [...items];
        this._cdr.markForCheck();
    }

    get datasource() {
        return this._datasource;
    }

    set value(source: any[]) {
        let map: any = {};
        for (const item of source) {
            map = { ...map, ...item };
        }
        this.formControl.setValue(map);
    }

    isDisabled() {
        return this.field.templateOptions.disabled;
    }

    isValid(field: FormlyFieldConfig) {
        if (field.key) {
            let requiredValidation = this.required ? field.formControl.value.length > 0 : true;
            return field.formControl.valid && requiredValidation;
        }
        return field.fieldGroup ? field.fieldGroup.every((f) => this.isValid(f)) : true;
    }

    removeItem(item) {
        this.value = this.datasource.filter((fileName) => fileName !== item);
    }

    public dropped(files: NgxFileDropEntry[]) {
        if (this.maxNumberOfFiles && files.length > this.maxNumberOfFiles) {
            this._notifyService.error(
                this._translocoService.translate("formly-file-uploader.Max number of files exceed")
            );
            return;
        }

        if (this.fileTypes) {
            let filtered = files;
            this.fileTypes.forEach((type) => {
                filtered = filtered.filter((file) => !file.fileEntry.name.includes(type));
            });

            this.filesWithTypeError = filtered;

            if (this.filesWithTypeError.length > 0) {
                this._notifyService.error(
                    this._translocoService.translate("formly-file-uploader.File doesn't match selected type")
                );
                return;
            }
        }

        for (const droppedFile of files) {
            if (droppedFile.fileEntry.isFile) {
                const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
                fileEntry.file((file: File) => {
                    this.loading$.next(true);
                    let url = this._url + `/api/services/app/TemporalFileManagerService/Send`;
                    const content_ = new FormData();
                    if (file === null || file === undefined) throw new Error("The parameter 'file' cannot be null.");
                    else content_.append("file", file, file.name);

                    let options: any = {
                        body: content_,
                        observe: "response",
                        responseType: "blob",
                        headers: new HttpHeaders({
                            Accept: "text/plain",
                        }),
                    };

                    this._http
                        .request("post", url, options)
                        .pipe(finalize(() => this.loading$.next(false)))
                        .subscribe((response) => {
                            if (response instanceof HttpResponseBase) {
                                this.proccess(response).subscribe((result) => {
                                    this.value = [...this.datasource, { [`${result.name}`]: result.url }];
                                });
                            }
                        });
                });
            }
        }
    }

    proccess(response: HttpResponseBase) {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse
                ? response.body
                : (<any>response).error instanceof Blob
                    ? (<any>response).error
                    : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);

                    return _observableOf(resultData200);
                })
            );
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return throwException("An unexpected server error occurred.", status, _responseText, _headers);
                })
            );
        }
        return _observableOf<any>(<any>null);
    }
}

export class ApiException extends Error {
    message: string;
    status: number;
    response: string;
    headers: { [key: string]: any };
    result: any;

    constructor(message: string, status: number, response: string, headers: { [key: string]: any }, result: any) {
        super();

        this.message = message;
        this.status = status;
        this.response = response;
        this.headers = headers;
        this.result = result;
    }

    protected isApiException = true;

    static isApiException(obj: any): obj is ApiException {
        return obj.isApiException === true;
    }
}

function throwException(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result?: any
): Observable<any> {
    if (result !== null && result !== undefined) return _observableThrow(result);
    else return _observableThrow(new ApiException(message, status, response, headers, null));
}

function blobToText(blob: any): Observable<string> {
    return new Observable<string>((observer: any) => {
        if (!blob) {
            observer.next("");
            observer.complete();
        } else {
            let reader = new FileReader();
            reader.onload = (event) => {
                observer.next((<any>event.target).result);
                observer.complete();
            };
            reader.readAsText(blob);
        }
    });
}
